// src/components/VictorPage.jsx
// Victor Strategy Studio — Deal coaching, pursuit strategy, pipeline review,
// and weekly priorities. Four tabs, one AI, all grounded in live Notion data.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useProfile } from '../context/UserProfileContext';
import './VictorPage.css';
import { apiFetch } from '../lib/api';
import { completeQueueItem, dismissQueueItem } from '../lib/situationQueue';

// ─────────────────────────────────────────────────────────────────────────────
// RESEARCH REQUEST DETECTION
// Scans a Victor message for patterns that indicate a research request.
// Returns { detected: bool, summary: string } — summary is shown in the
// Send to Selma action bar so Gary knows what will be queued.
// ─────────────────────────────────────────────────────────────────────────────

const RESEARCH_PATTERNS = [
  /selma\s+(should|needs? to|can|could|would)\s+(research|investigate|find|look up|pull|gather|check)/i,
  /task\s+selma\s+(to|with)/i,
  /research\s+(request|needed|required|gap)/i,
  /intelligence\s+gap/i,
  /we\s+need\s+(more\s+)?(intel|intelligence|research|data|information)\s+on/i,
  /before\s+(reaching|we\s+reach|outreach|approaching)\s+.*\s+(we\s+)?need/i,
  /recommend\s+(that\s+)?selma/i,
  /have\s+selma\s+(look|check|find|pull|research)/i,
  /worth\s+(researching|investigating|knowing)/i,
  /gap[s]?\s+(in\s+our\s+)?(intelligence|intel|data|knowledge)/i,
  /find\s+(out|more\s+about|who\s+is|the)\s+(their|this\s+account)/i,
  /executive\s+(research|search|intel)/i,
  /buying\s+committee/i,
];

function detectResearchRequest(text) {
  if (!text || text.length < 40) return { detected: false, summary: '' };
  const matched = RESEARCH_PATTERNS.some(p => p.test(text));
  if (!matched) return { detected: false, summary: '' };

  // Pull a short summary: find the first sentence containing a trigger word
  const sentences = text.split(/(?<=[.!?])\s+/);
  const trigger   = sentences.find(s => RESEARCH_PATTERNS.some(p => p.test(s))) || sentences[0];
  const summary   = trigger.replace(/\*\*/g, '').slice(0, 120).trim();

  return { detected: true, summary };
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'deal_coach',  icon: '🎯', label: 'Deal Coach' },
  { id: 'pursuit',     icon: '🔍', label: 'Pursuit Strategy' },
  { id: 'pipeline',    icon: '📊', label: 'Pipeline Advisor' },
  { id: 'priorities',  icon: '⚡', label: 'Weekly Priorities' },
];

const STARTER_PROMPTS = {
  deal_coach: [
    'Where is this deal most at risk right now?',
    'How do I identify and build a champion here?',
    'Score my MEDDIC for this opportunity',
    'What should I focus on in my next meeting?',
    'Is this deal worth pursuing at full effort?',
  ],
  pursuit: [
    'Should I pursue this account now or wait?',
    'What is the best entry motion given what we know?',
    'Who should I target first at this account?',
    'What intelligence am I missing before reaching out?',
    'How fresh are the signals driving this pursuit?',
  ],
  pipeline: [
    'Which deals in my pipeline are most at risk?',
    'Where should I focus my energy this week?',
    'Which opportunities have weak MEDDIC scores?',
    'What deals are most likely to close this quarter?',
    'Are there deals I should walk away from?',
  ],
  priorities: [
    'What should be my top 3 priorities this week?',
    'Which accounts need attention based on signal age?',
    'What follow-ups are overdue across my pipeline?',
    'Where is the highest-probability near-term revenue?',
    'What strategic moves should I make this week?',
  ],
};

const MODE_DESCRIPTIONS = {
  deal_coach:  'Select an account and opportunity. Victor coaches you on this specific deal.',
  pursuit:     'Select an account with signals. Victor advises on whether and how to pursue.',
  pipeline:    'No account needed. Victor reviews your full pipeline and flags risks.',
  priorities:  'No account needed. Victor synthesizes your week\'s strategic priorities.',
};

// ─────────────────────────────────────────────────────────────────────────────
// VICTOR AVATAR
// ─────────────────────────────────────────────────────────────────────────────

function VictorAvatar({ size = 40, thinking = false }) {
  return (
    <div
      className={`victor-avatar${thinking ? ' victor-avatar--thinking' : ''}`}
      style={{ width: size, height: size, borderRadius: size * 0.28 }}
    >
      <span style={{ fontSize: size * 0.38, fontWeight: 900, color: 'var(--dm-white)', letterSpacing: '-1px' }}>V</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MEDDIC MINI SCORES (shown in Deal Coach sidebar)
// ─────────────────────────────────────────────────────────────────────────────



// ─────────────────────────────────────────────────────────────────────────────
// SIGNAL PILL
// ─────────────────────────────────────────────────────────────────────────────

function SignalPill({ signal }) {
  const freshnessColor = {
    fresh:      'var(--dm-green)',
    recent:     'var(--dm-amber)',
    aging:      '#f97316',
    stale:      'var(--dm-red)',
    'very-stale': '#991b1b',
    unknown:    'var(--dm-grey-600)',
  };

  const freshnessLabel = {
    fresh:        '🔥 Fresh',
    recent:       '⚡ Recent',
    aging:        '⚠️ Aging',
    stale:        '🕰️ Stale',
    'very-stale': '🪦 Very Stale',
    unknown:      '? Unknown',
  };

  return (
    <div className="victor-signal-pill">
      <div className="victor-signal-pill-top">
        <span className="victor-signal-name">{signal.name}</span>
        <span
          className="victor-signal-age"
          style={{ color: freshnessColor[signal.freshness] || 'var(--dm-grey-600)' }}
        >
          {freshnessLabel[signal.freshness] || signal.freshness}
        </span>
      </div>
      {signal.ageInDays != null && (
        <span className="victor-signal-days">{signal.ageInDays} days old</span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACCOUNT / OPPORTUNITY SELECTORS
// ─────────────────────────────────────────────────────────────────────────────

function AccountSelector({ value, onChange }) {
  const [accounts, setAccounts]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState('');

  useEffect(() => {
    setLoading(true);
    apiFetch('/api/accounts/list?page_size=100')
      .then(r => r.json())
      .then(d => {
        if (d.accounts) setAccounts(d.accounts);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = accounts.filter(a =>
    a.accountName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="victor-selector">
      <label className="victor-selector-label">Account</label>
      <input
        className="victor-selector-search"
        placeholder="Search accounts..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      <div className="victor-selector-list">
        {loading && <div className="victor-selector-empty">Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div className="victor-selector-empty">No accounts found</div>
        )}
        {filtered.map(a => (
          <button
            key={a.id}
            className={`victor-selector-item ${value?.id === a.id ? 'victor-selector-item--active' : ''}`}
            onClick={() => onChange({ ...a, name: a.accountName })}
            type="button"
          >
            <span className="victor-selector-item-name">{a.accountName}</span>
            {a.tier && <span className="victor-selector-item-meta">{a.tier}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function OpportunitySelector({ accountId, value, onChange }) {
  const [opps, setOpps]       = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    apiFetch(`/api/opportunities/by-account?accountId=${accountId}`)
      .then(r => r.json())
      .then(d => {
        if (d.opportunities) setOpps(d.opportunities);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [accountId]);

  if (!accountId) return null;

  return (
    <div className="victor-selector victor-selector--opp">
      <label className="victor-selector-label">Opportunity (optional)</label>
      <div className="victor-selector-list victor-selector-list--short">
        <button
          className={`victor-selector-item ${!value ? 'victor-selector-item--active' : ''}`}
          onClick={() => onChange(null)}
          type="button"
        >
          <span className="victor-selector-item-name">— No specific opportunity</span>
        </button>
        {loading && <div className="victor-selector-empty">Loading...</div>}
        {opps.map(o => (
          <button
            key={o.id}
            className={`victor-selector-item ${value?.id === o.id ? 'victor-selector-item--active' : ''}`}
            onClick={() => onChange(o)}
            type="button"
          >
            <span className="victor-selector-item-name">{o.name}</span>
            <span className="victor-selector-item-meta">{o.probability?.split(' - ')[1] || o.stage}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHAT MESSAGE
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// SEND TO SELMA ACTION BAR
// Appears beneath Victor messages that contain a research request.
// ─────────────────────────────────────────────────────────────────────────────

function SendToSelmaBar({ account, messageText, onQueued }) {
  const [state,   setState]   = useState('idle');
  const [errMsg,  setErrMsg]  = useState('');

  const handleSend = async () => {
    if (!account?.name) return;
    setState('loading');
    try {
      const res = await apiFetch('/api/research/queue-from-victor', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId:    account.id   || null,
          accountName:  account.name,
          instructions: messageText.slice(0, 1500),
          sessionNote:  '',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setState(data.status === 'already_queued' ? 'already' : 'success');
        onQueued?.(data);
      } else {
        setErrMsg(data.error || `HTTP ${res.status}`);
        setState('error');
      }
    } catch (err) {
      setErrMsg(err.message || 'Network error');
      setState('error');
    }
  };

  if (state === 'success') {
    return (
      <div className="victor-selma-bar victor-selma-bar--success">
        <span className="victor-selma-bar-icon">✓</span>
        <span className="victor-selma-bar-text">
          <strong>{account.name}</strong> added to Selma's research queue.
          Victor's instructions saved as a note.
        </span>
      </div>
    );
  }

  if (state === 'already') {
    return (
      <div className="victor-selma-bar victor-selma-bar--already">
        <span className="victor-selma-bar-icon">🔄</span>
        <span className="victor-selma-bar-text">
          <strong>{account.name}</strong> is already queued — Victor's instructions updated.
        </span>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="victor-selma-bar victor-selma-bar--error">
        <span className="victor-selma-bar-icon">⚠</span>
        <span className="victor-selma-bar-text">{errMsg || 'Could not queue research.'}</span>
        <button className="victor-selma-retry" onClick={() => setState('idle')} type="button">Retry</button>
      </div>
    );
  }

  return (
    <div className="victor-selma-bar">
      <div className="victor-selma-bar-left">
        <span className="victor-selma-bar-icon">🔭</span>
        <div>
          <div className="victor-selma-bar-label">Victor identified a research need</div>
          <div className="victor-selma-bar-account">{account?.name || 'Select an account first'}</div>
        </div>
      </div>
      <button
        className="victor-selma-send-btn"
        onClick={handleSend}
        disabled={state === 'loading' || !account?.name}
        type="button"
      >
        {state === 'loading' ? (
          <><span className="victor-selma-spinner" />Sending…</>
        ) : (
          <>Send to Selma →</>
        )}
      </button>
    </div>
  );
}

function ChatMessage({ message, isThinking, account, onQueued }) {
  const isVictor  = message.role === 'assistant';
  const research  = isVictor && !isThinking && !message._streaming
    ? detectResearchRequest(message.content)
    : { detected: false };

  // Render markdown-ish formatting: bold, bullet points, headers
  const renderContent = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, i) => {
      if (line.startsWith('## ')) return <h3 key={i} className="victor-msg-h2">{line.slice(3)}</h3>;
      if (line.startsWith('# '))  return <h2 key={i} className="victor-msg-h1">{line.slice(2)}</h2>;
      if (line.startsWith('- ') || line.startsWith('• ')) {
        return (
          <div key={i} className="victor-msg-bullet">
            <span className="victor-msg-bullet-dot">▸</span>
            <span>{formatInline(line.slice(2))}</span>
          </div>
        );
      }
      if (line.match(/^\d+\. /)) {
        const num   = line.match(/^(\d+)\. /)[1];
        const rest  = line.replace(/^\d+\. /, '');
        return (
          <div key={i} className="victor-msg-numbered">
            <span className="victor-msg-num">{num}.</span>
            <span>{formatInline(rest)}</span>
          </div>
        );
      }
      if (line.trim() === '') return <div key={i} className="victor-msg-spacer" />;
      return <p key={i} className="victor-msg-p">{formatInline(line)}</p>;
    });
  };

  const formatInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <div className={`victor-msg-wrapper${research.detected ? ' victor-msg-wrapper--has-action' : ''}`}>
      <div className={`victor-msg ${isVictor ? 'victor-msg--ai' : 'victor-msg--user'}`}>
        {isVictor && (
          <div className="victor-msg-avatar">
            <VictorAvatar size={32} thinking={isThinking} />
          </div>
        )}
        <div className="victor-msg-bubble">
          {isThinking ? (
            <div className="victor-thinking">
              <span /><span /><span />
            </div>
          ) : (
            <div className="victor-msg-content">
              {renderContent(message.content)}
            </div>
          )}
        </div>
      </div>
      {research.detected && (
        <SendToSelmaBar
          account={account}
          messageText={message.content}
          onQueued={onQueued}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SIDEBAR — context panel shown alongside the chat
// ─────────────────────────────────────────────────────────────────────────────

function ContextSidebar({ tab, account, opportunity, signals, onChangeAccount, onChangeOpp }) {
  const needsAccount = tab === 'deal_coach' || tab === 'pursuit';

  return (
    <aside className="victor-sidebar">
      {/* Context section */}
      <div className="victor-sidebar-section">
        <div className="victor-sidebar-heading">Context</div>
        <p className="victor-sidebar-hint">{MODE_DESCRIPTIONS[tab]}</p>
      </div>

      {needsAccount && (
        <>
          <div className="victor-sidebar-section">
            <AccountSelector
              value={account}
              onChange={onChangeAccount}
            />
          </div>

          {tab === 'deal_coach' && account && (
            <div className="victor-sidebar-section">
              <OpportunitySelector
                accountId={account.id}
                value={opportunity}
                onChange={onChangeOpp}
              />
            </div>
          )}
        </>
      )}

      {/* Signals loaded for this account */}
      {account && signals.length > 0 && (
        <div className="victor-sidebar-section">
          <div className="victor-sidebar-heading">Signals Loaded</div>
          <div className="victor-signals-list">
            {signals.map((s, i) => (
              <SignalPill key={i} signal={s} />
            ))}
          </div>
        </div>
      )}

      {/* Opportunity quick facts */}
      {opportunity && (
        <div className="victor-sidebar-section">
          <div className="victor-sidebar-heading">Opportunity</div>
          <div className="victor-opp-facts">
            {opportunity.probability && (
              <div className="victor-opp-fact">
                <span className="victor-opp-fact-label">Stage</span>
                <span className="victor-opp-fact-value">{opportunity.probability?.split(' - ')[1] || opportunity.stage}</span>
              </div>
            )}
            {opportunity.dealSize && (
              <div className="victor-opp-fact">
                <span className="victor-opp-fact-label">Deal Size</span>
                <span className="victor-opp-fact-value">{opportunity.dealSize}</span>
              </div>
            )}
            {opportunity.priority && (
              <div className="victor-opp-fact">
                <span className="victor-opp-fact-label">Priority</span>
                <span className="victor-opp-fact-value">{opportunity.priority}</span>
              </div>
            )}
            {opportunity.impactArea && (
              <div className="victor-opp-fact">
                <span className="victor-opp-fact-label">Impact</span>
                <span className="victor-opp-fact-value">{opportunity.impactArea}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pipeline/priorities: no account needed note */}
      {(tab === 'pipeline' || tab === 'priorities') && (
        <div className="victor-sidebar-section">
          <div className="victor-pipeline-note">
            <span className="victor-pipeline-note-icon">📊</span>
            {/* True as of B69 — before that this promised a pipeline the handler
                never loaded. "Highest-priority" rather than "full" because
                api/victor/chat.js caps the list (PIPELINE_CAP); the cap is above
                a typical book, so nothing is dropped today, but the copy should
                not overclaim for a tenant that outgrows it. */}
            <p>Victor loads your highest-priority active opportunities for this mode.</p>
          </div>
        </div>
      )}
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHAT PANEL
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// QUEUE PANEL — shown in Priorities tab above the chat
// ─────────────────────────────────────────────────────────────────────────────
// Exported for src/components/victorQueueActions.test.js (P1.5 IA-1d). It is
// rendered only on the 'priorities' tab, so reaching it through VictorPage
// would mean driving tab state to assert on a mutation this component owns
// outright. The default export is unchanged.
export function QueuePanel({ onNavigate }) {
  const [active,    setActive]    = useState([]);
  const [handled,   setHandled]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  // Separate from `error`, which replaces the WHOLE panel (:610) — correct for a
  // failed load, wrong for a failed action. A complete/dismiss that fails must
  // leave the queue on screen with its rows intact and say what went wrong.
  const [actionError, setActionError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await apiFetch('/api/victor/situation-queue?view=full');
      const data = await res.json();
      if (data.success) {
        setActive(data.active  || []);
        setHandled(data.handled || []);
      } else {
        setError('Could not load queue');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // The request and the error rule live in src/lib/situationQueue.js — this
  // endpoint has three callers and had three private copies of both, two of
  // which treated a failed write as a success (P1.5 IA-8a).
  const handleAct = async (item) => {
    try {
      await completeQueueItem(item.id);
    } catch (e) {
      // Navigating away on a failed write would hide it completely — the user
      // would land on the Floor and never learn the item was not marked done.
      setActionError(`Could not mark "${item.action}" as actioned — ${e.message}`);
      return;
    }
    setActionError(null);
    setActive(prev => prev.filter(i => i.id !== item.id));
    setHandled(prev => [{ ...item, status: 'Actioned', actionedAt: new Date().toISOString() }, ...prev]);
    onNavigate && onNavigate('floor', null, {
      type: 'queue', action: item.action,
      accountId: item.accountId || undefined,
      accountName: item.accountName || undefined,
      preAnalysis: item.why || '',
      returnTo: 'victor',
    });
  };

  const handleArchive = async (item) => {
    try {
      await dismissQueueItem(item.id);
    } catch (e) {
      setActionError(`Could not dismiss "${item.action}" — ${e.message}`);
      return;
    }
    setActionError(null);
    setActive(prev => prev.filter(i => i.id !== item.id));
  };

  const PRIORITY_STYLES = {
    '🔴 Hot':       { bg: 'var(--dm-red-light)', color: '#991b1b', label: 'Hot' },
    '🟡 Important': { bg: 'var(--dm-amber-light)', color: '#92400e', label: 'Important' },
    '📌 Watch':     { bg: 'var(--dm-blue-light)', color: '#1e40af', label: 'Watch' },
    '🔵 Watch':     { bg: 'var(--dm-blue-light)', color: '#1e40af', label: 'Watch' },
  };

  const PBadge = ({ priority }) => {
    const s = PRIORITY_STYLES[priority] || { bg: '#f3f4f6', color: 'var(--dm-grey-600)', label: priority || 'Watch' };
    return (
      <span className="vq-badge" style={{ background: s.bg, color: s.color }}>{s.label}</span>
    );
  };

  if (loading) return (
    <div className="vq-panel vq-loading">Loading queue...</div>
  );
  if (error) return (
    <div className="vq-panel vq-error">{error} <button onClick={load}>Retry</button></div>
  );

  const openItems   = active.filter(i => !i.inToday);
  const todayItems  = active.filter(i =>  i.inToday);

  return (
    <div className="vq-panel">
      <div className="vq-header">
        <span className="vq-header-title">Action Queue</span>
        <button className="vq-refresh" onClick={load} title="Refresh">↻</button>
      </div>

      {actionError && (
        <div className="vq-action-error" role="alert">
          ⚠️ {actionError}
          <button className="vq-action-error-dismiss"
                  onClick={() => setActionError(null)}
                  aria-label="Dismiss message">×</button>
        </div>
      )}

      {/* In Today's Priorities */}
      {todayItems.length > 0 && (
        <div className="vq-section">
          <div className="vq-section-label">In Today's Priorities</div>
          {todayItems.map(item => (
            <div key={item.id} className="vq-item vq-item--today" onClick={() => handleAct(item)}>
              <div className="vq-item-left">
                <PBadge priority={item.priority} />
                <span className="vq-today-badge">Today</span>
              </div>
              <div className="vq-item-body">
                <div className="vq-item-action">{item.action}</div>
                {item.why && <div className="vq-item-why">{item.why}</div>}
              </div>
              <span className="vq-item-arrow">→</span>
            </div>
          ))}
        </div>
      )}

      {/* Open — not yet in Today */}
      {openItems.length > 0 && (
        <div className="vq-section">
          <div className="vq-section-label">Open{openItems.length > 0 ? ` — ${openItems.length}` : ''}</div>
          {openItems.map(item => (
            <div key={item.id} className="vq-item" onClick={() => handleAct(item)}>
              <PBadge priority={item.priority} />
              <div className="vq-item-body">
                <div className="vq-item-action">{item.action}</div>
                {item.why && <div className="vq-item-why">{item.why}</div>}
              </div>
              <div className="vq-item-actions">
                <span className="vq-item-arrow">→</span>
                <button
                  className="vq-archive-btn"
                  title="Archive"
                  onClick={e => { e.stopPropagation(); handleArchive(item); }}
                >✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Handled — Actioned or Snoozed */}
      {handled.length > 0 && (
        <div className="vq-section vq-section--handled">
          <div className="vq-section-label">Handled</div>
          {handled.map(item => (
            <div key={item.id} className="vq-item vq-item--handled">
              <PBadge priority={item.priority} />
              <div className="vq-item-body">
                <div className="vq-item-action">{item.action}</div>
              </div>
              <span className={`vq-status-badge vq-status--${item.status === 'Actioned' ? 'done' : 'snoozed'}`}>
                {item.status === 'Actioned' ? '✓ Done' : `Snoozed to ${item.snoozedUntil}`}
              </span>
            </div>
          ))}
        </div>
      )}

      {active.length === 0 && handled.length === 0 && (
        <div className="vq-empty">Queue is clear — Victor will add items tonight.</div>
      )}
    </div>
  );
}

function ChatPanel({ tab, account, opportunity, signals, pastSessions, initialContext, onNavigate }) {
  // P0.30 30.2 — the persona's own name, not a pronoun. From context, not a
  // prop: ChatPanel is rendered in several places and threading it would mean
  // touching each call site for a string the context already has.
  const { strategyName } = useProfile();
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState(initialContext || '');
  const [streaming, setStreaming] = useState(false);
  const [error,     setError]     = useState(null);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);
  const abortRef   = useRef(null);

  // Keep a ref to current messages/account/tab so the cleanup function can read them
  const messagesRef = useRef([]);
  const accountRef  = useRef(null);
  const tabRef      = useRef(tab);

  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { accountRef.current  = account;  }, [account]);
  useEffect(() => { tabRef.current      = tab;      }, [tab]);

  const needsAccount = (tab === 'deal_coach' || tab === 'pursuit') && !account;

  // Auto-save helper — fire and forget, no UI feedback needed
  const autoSave = useCallback((msgs, acct, t) => {
    if (!msgs || msgs.length < 2) return;

    // Jarvis session-close — runs in parallel with Victor's own save
    // Gives Jarvis cross-room visibility into Victor Studio sessions
    apiFetch('/api/jarvis/session-close', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        thread: msgs.map(m => ({ role: m.role, ai: m.role === 'assistant' ? 'victor' : undefined, content: m.content, timestamp: m.timestamp })),
        context: { accountId: acct?.id || null, accountName: acct?.name || '' },
        sessionType: 'victor',
        actionsLog: [], proposedQueueItems: [],
      }),
    }).catch(() => {});

    apiFetch('/api/victor/save-session', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accountId:   acct?.id   || null,
        accountName: acct?.name || '',
        messages:    msgs,
        mode:        t,
      }),
    }).catch(() => {}); // silent — don't surface errors for auto-save
  }, []);

  // Auto-save and reset when tab or account changes
  useEffect(() => {
    return () => {
      // This cleanup runs BEFORE the next render with new tab/account values
      autoSave(messagesRef.current, accountRef.current, tabRef.current);
    };
  }, [tab, account?.id, autoSave]);

  // Reset conversation state when tab or account changes
  useEffect(() => {
    setMessages([]);
    setInput('');
    setError(null);
    if (abortRef.current) abortRef.current.abort();
  }, [tab, account?.id, opportunity?.id]);

  // Auto-save on component unmount (leaving Victor page)
  useEffect(() => {
    return () => {
      autoSave(messagesRef.current, accountRef.current, tabRef.current);
    };
  }, [autoSave]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  const sendMessage = useCallback(async (text) => {
    const content = (text || input).trim();
    if (!content || streaming) return;

    setInput('');
    setError(null);

    const userMsg = { role: 'user', content };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setStreaming(true);

    // Add placeholder for Victor's response
    setMessages(prev => [...prev, { role: 'assistant', content: '', _streaming: true }]);

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const res = await apiFetch('/api/victor/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          messages:       newMessages,
          accountId:      account?.id || null,
          accountName:    account?.name || '',
          opportunityId:  opportunity?.id || null,
          mode:           tab,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const fullText = data.message?.content || data.text || '';

      if (!fullText) throw new Error('Empty response from Victor');

      // Finalize — replace streaming placeholder with real response
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: fullText };
        return updated;
      });

      // Jarvis observation — fire and forget
      apiFetch('/api/jarvis/observe', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room: 'victor_studio', ai: 'victor', sessionType: 'victor',
          accountId:     account?.id || null,
          opportunityId: opportunity?.id || null,
          response:      fullText,
          context: { accountName: account?.name || null },
          thread: newMessages.slice(-6),
        }),
      }).catch(() => {});

    } catch (err) {
      if (err.name === 'AbortError') return;
      setError('Victor encountered an error. Try again.');
      console.error('[victor/chat]', err);
      // Remove placeholder
      setMessages(prev => prev.filter(m => !m._streaming));
    } finally {
      setStreaming(false);
    }
  }, [input, messages, streaming, tab, account, opportunity]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const starters = STARTER_PROMPTS[tab] || [];

  return (
    <div className="victor-chat">
      {/* Past session memory indicator */}
      {pastSessions && pastSessions.length > 0 && (
        <div className="victor-memory-banner">
          <span className="victor-memory-icon">🧠</span>
          <span className="victor-memory-text">
            Victor has memory from {pastSessions.length} past session{pastSessions.length > 1 ? 's' : ''} with {account?.name}
          </span>
        </div>
      )}
      {/* Queue Panel — only shown on Priorities tab */}
      {tab === 'priorities' && (
        <QueuePanel onNavigate={onNavigate} />
      )}

      {/* Empty state / starters */}
      {messages.length === 0 && (
        <div className="victor-empty">
          {needsAccount ? (
            <div className="victor-empty-blocked">
              <VictorAvatar size={48} />
              <p className="victor-empty-blocked-text">Select an account on the left to begin.</p>
            </div>
          ) : (
            <>
              <VictorAvatar size={48} />
              <h3 className="victor-empty-title">
                {tab === 'deal_coach'  && `Deal Coach${account ? ` — ${account.name}` : ''}`}
                {tab === 'pursuit'     && `Pursuit Strategy${account ? ` — ${account.name}` : ''}`}
                {tab === 'pipeline'    && 'Pipeline Advisor'}
                {tab === 'priorities'  && 'Weekly Priorities'}
              </h3>
              <p className="victor-empty-sub">
                {tab === 'deal_coach'  && 'Ask ' + strategyName + ' anything about this deal. Loaded: account signals, contacts, and opportunity context.'}
                {tab === 'pursuit'     && 'Victor will advise on pursuit timing, entry motion, and target contacts — grounded in signal freshness.'}
                {tab === 'pipeline'    && 'Victor has loaded your full active pipeline. Ask about risks, priorities, and deal health.'}
                {tab === 'priorities'  && "Victor will synthesize this week's highest-leverage moves across your pipeline and signals."}
              </p>
              <div className="victor-starters">
                {starters.map((s, i) => (
                  <button
                    key={i}
                    className="victor-starter"
                    onClick={() => sendMessage(s)}
                    type="button"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Messages */}
      {messages.length > 0 && (
        <div className="victor-messages">
          {messages.map((msg, i) => (
            <ChatMessage
              key={i}
              message={msg}
              isThinking={msg._streaming && msg.content === ''}
              account={account}
              onQueued={() => {/* Selma notified */}}
            />
          ))}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="victor-chat-error">⚠️ {error}</div>
      )}

      {/* Input */}
      <div className="victor-input-area">
        <div className="victor-input-wrap">
          <textarea
            ref={inputRef}
            className="victor-input"
            placeholder={
              needsAccount
                ? 'Select an account first...'
                : `Ask Victor about ${tab === 'deal_coach' ? 'this deal' : tab === 'pursuit' ? 'this pursuit' : 'your pipeline'}...`
            }
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={streaming || needsAccount}
            rows={1}
          />
          <button
            className={`victor-send ${streaming ? 'victor-send--streaming' : ''}`}
            onClick={() => sendMessage()}
            disabled={!input.trim() || streaming || needsAccount}
            type="button"
            title="Send (Enter)"
          >
            {streaming ? (
              <span className="victor-send-stop">■</span>
            ) : (
              <span className="victor-send-arrow">↑</span>
            )}
          </button>
        </div>
        {messages.length > 0 && (
          <button
            className="victor-clear"
            onClick={() => { setMessages([]); setError(null); }}
            type="button"
          >
            Clear conversation
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function VictorPage({ onNavigate, initialContext, preloadedContext }) {
  const { strategyName } = useProfile();
  const [activeTab,    setActiveTab]    = useState(preloadedContext?.mode || 'deal_coach');
  const [account,      setAccount]      = useState(null);
  const [opportunity,  setOpportunity]  = useState(null);
  const [signals,      setSignals]      = useState([]);
  const [pastSessions, setPastSessions] = useState([]);

  // Auto-load account + opportunity when navigated from opportunity card
  useEffect(() => {
    if (!preloadedContext?.accountId) return;
    // Fetch the account record to populate the selector
    apiFetch('/api/accounts/detail?id=' + preloadedContext.accountId)
      .then(r => r.json())
      .then(d => {
        if (d.account) {
          setAccount({ ...d.account, id: preloadedContext.accountId });
        }
      })
      .catch(err => console.warn('[VictorPage] preloadedContext account fetch failed:', err));
    // Set opportunity stub — full data loads when chat fires
    if (preloadedContext.opportunityId) {
      setOpportunity({ id: preloadedContext.opportunityId, name: preloadedContext.opportunityName || 'Selected Opportunity' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load signals whenever account changes
  useEffect(() => {
    if (!account?.id) { setSignals([]); return; }
    apiFetch('/api/signals/by-account?accountId=' + account.id)
      .then(r => r.json())
      .then(d => { if (d.signals) setSignals(d.signals); })
      .catch(() => setSignals([]));
  }, [account?.id]);

  // Load past Victor sessions whenever account changes
  useEffect(() => {
    if (!account?.id) { setPastSessions([]); return; }
    apiFetch('/api/victor/past-sessions?accountId=' + account.id)
      .then(r => r.json())
      .then(d => { if (d.sessions) setPastSessions(d.sessions); })
      .catch(() => setPastSessions([]));
  }, [account?.id]);

  const handleAccountChange = (acct) => {
    setAccount(acct);
    setOpportunity(null);
    setSignals([]);
    setPastSessions([]);
  };

  return (
    <div className="victor-root">

      {/* ── Page header ── */}
      <div className="victor-page-header">
        <div className="victor-page-header-left">
          {/* ♟️ — the same glyph as this page's AI-strip button. */}
          <span className="dm-page-mark dm-page-mark--victor">♟️</span>
          <div>
            <div className="victor-page-title">{strategyName || 'Victor'} Strategy Studio</div>
            <div className="victor-page-sub">Deal coaching grounded in real account intelligence</div>
          </div>
        </div>
        <div className="victor-page-header-right">
          <div className="victor-online-badge">
            <div className="victor-online-dot" />
            Online
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="victor-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`victor-tab ${activeTab === tab.id ? 'victor-tab--active' : ''}`}
            onClick={(e) => {
              setActiveTab(tab.id);
              // Same as Cyrano and Comms: the row scrolls at narrow widths, so
              // centre the tab just chosen or it can sit half-cut at the edge
              // with no way to see which is active.
              e.currentTarget.scrollIntoView?.({ block: 'nearest', inline: 'center' });
            }}
            type="button"
          >
            <span className="victor-tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Main layout: sidebar + chat ── */}
      <div className="victor-layout">
        <ContextSidebar
          tab={activeTab}
          account={account}
          opportunity={opportunity}
          signals={signals}
          onChangeAccount={handleAccountChange}
          onChangeOpp={setOpportunity}
        />
        <ChatPanel
          tab={activeTab}
          account={account}
          opportunity={opportunity}
          signals={signals}
          pastSessions={pastSessions}
          initialContext={initialContext}
          onNavigate={onNavigate}
        />
      </div>

    </div>
  );
}
